import { useCallback, useState } from 'react';

import { MAX_INPUT_LENGTH, sanitizeInputText } from './constants';

/**
 * Manages slash-menu dropdown state and keyboard navigation for a textarea field.
 *
 * @param {object[]} options         - Menu options with { text, label }
 * @param {string}   value           - Current field value (controlled)
 * @param {Function} setValue        - State setter for the field value
 * @param {Function} onFieldEdit     - Callback to mark the field as user-edited
 * @param {string}   fieldName       - Field key passed to onFieldEdit
 * @param {boolean}  isDisplayMode   - When true, change events are no-ops
 * @param {Function} onAfterSelect   - Optional: called with the selected option after selection
 * @param {Function} onAfterChange   - Optional: called after every value change (incl. close-on-slash)
 * @param {number}   maxLength       - Max character limit for this field
 */
const useSlashMenuField = ({
  options,
  value,
  setValue,
  onFieldEdit,
  fieldName,
  isDisplayMode = false,
  onAfterSelect = null,
  onAfterChange = null,
  maxLength = MAX_INPUT_LENGTH,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openedFromTrigger, setOpenedFromTrigger] = useState(false);

  const previewText = dropdownOpen && highlightedIndex >= 0
    ? options[highlightedIndex].text
    : null;

  const handleSelect = option => {
    onFieldEdit?.(fieldName);
    setValue(sanitizeInputText(option.text, maxLength));
    onAfterSelect?.(option);
    setDropdownOpen(false);
    setHighlightedIndex(-1);
    setOpenedFromTrigger(false);
  };

  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
    setHighlightedIndex(-1);
    setOpenedFromTrigger(false);

    if (value === '/') {
      onFieldEdit?.(fieldName);
      setValue('');
      onAfterChange?.('');
    }
  }, [value, onFieldEdit, fieldName, setValue, onAfterChange]);

  const openFromTrigger = () => {
    if (dropdownOpen && openedFromTrigger) {
      closeDropdown();
    }
    else {
      setDropdownOpen(true);
      setHighlightedIndex(-1);
      setOpenedFromTrigger(true);
    }
  };

  const handleChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitized = sanitizeInputText(event.target.value, maxLength);

    onFieldEdit?.(fieldName);
    setValue(sanitized);
    onAfterChange?.(sanitized);

    if (sanitized === '/') {
      setDropdownOpen(true);
      setHighlightedIndex(-1);
      setOpenedFromTrigger(false);
    }
    else if (dropdownOpen) {
      setDropdownOpen(false);
      setHighlightedIndex(-1);
      setOpenedFromTrigger(false);
    }
  };

  const handleKeyDown = e => {
    if (!dropdownOpen) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, options.length - 1));
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, -1));
    }
    else if (e.key === 'Enter') {
      e.preventDefault();

      if (highlightedIndex >= 0) {
        handleSelect(options[highlightedIndex]);
      }
    }
    else if (e.key === 'Escape') {
      closeDropdown();
    }
  };

  return {
    dropdownOpen,
    openedFromTrigger,
    highlightedIndex,
    setHighlightedIndex,
    previewText,
    handleSelect,
    closeDropdown,
    openFromTrigger,
    handleChange,
    handleKeyDown,
  };
};

export default useSlashMenuField;
